/**
 * The `/platform` layout: the guard, the socket claim, and the nav between the
 * surface's pages.
 *
 * Split out of `platform/index.tsx` when the surface gained a second page
 * (TECH-3244). The guard lived in the page component while there was only one
 * page; leaving it there would have meant every new page re-deciding who may see
 * it, which is exactly the kind of check that gets forgotten once.
 *
 * Unlike `/operations`, this surface runs on the `/app` session. Platform admin
 * is decided by the email a `@htctw.org` employee is already signed in as, so a
 * second login would be ceremony with nothing behind it. The client check here is
 * cosmetic, because every `platform.*` procedure carries `.requirePlatformAdmin()`
 * and the server decides on the same suffix (`lib/platform-admin.ts`).
 */
import { createFileRoute, Link, Outlet } from "@tanstack/react-router";
import { useEffect } from "react";
import { useStore } from "zustand";
import { isElectron } from "@/lib/platform";
import { claimSocket } from "@/lib/socket-owner";
import { authStore } from "@/lib/stores/auth-store";
import { cn } from "@/lib/utils";

const PLATFORM_ADMIN_EMAIL_SUFFIX = "@htctw.org";

export const Route = createFileRoute("/platform")({
	head: () => ({ meta: [{ title: "Platform - ELASO" }] }),
	component: RouteComponent,
});

function isPlatformAdminEmail(email: string | null | undefined): boolean {
	return email?.toLowerCase().endsWith(PLATFORM_ADMIN_EMAIL_SUFFIX) ?? false;
}

const PAGES = [
	{ to: "/platform", label: "Overview" },
	{ to: "/platform/workspaces", label: "Workspaces" },
] as const;

function RouteComponent() {
	const user = useStore(authStore, (state) => state.user);
	const initialized = useStore(authStore, (state) => state.initialized);
	const wsConnected = useStore(authStore, (state) => state.wsConnected);

	// Platform admin runs on the `/app` session, so claim the socket for it. Declared
	// before the early returns below so the hook order stays stable.
	useEffect(() => {
		claimSocket("app").catch((error) => {
			console.error("Failed to claim the socket for /app:", error);
		});
	}, []);

	if (isElectron) {
		return (
			<NotAvailable message="Platform operations are only available in the web app." />
		);
	}

	if (!initialized) {
		return <NotAvailable message="Loading..." />;
	}

	if (!(user && isPlatformAdminEmail(user.email))) {
		return <NotAvailable message="Not authorized." />;
	}

	if (!wsConnected) {
		return <NotAvailable message="Connecting to server..." />;
	}

	return (
		// The root layout clips routes to the viewport (h-svh + overflow-hidden),
		// so this surface scrolls itself.
		<div className="flex h-svh flex-col overflow-y-auto">
			<nav className="flex shrink-0 items-center gap-1 border-b px-8 py-3">
				{PAGES.map((page) => (
					<Link
						activeOptions={{ exact: true }}
						activeProps={{ className: "bg-muted text-foreground" }}
						className={cn(
							"rounded-md px-3 py-1.5 text-muted-foreground text-sm hover:text-foreground"
						)}
						key={page.to}
						to={page.to}
					>
						{page.label}
					</Link>
				))}
			</nav>
			<Outlet />
		</div>
	);
}

function NotAvailable({ message }: { message: string }) {
	return (
		<div className="flex h-svh items-center justify-center p-8">
			<p className="text-muted-foreground">{message}</p>
		</div>
	);
}
